import {autoClean, findMyDevice, resetMyDevice, updateDeviceSetting} from 'iqos-mw';

import {showToast} from '@/components/Toaster/Toaster';
import {DEVICE_SETTINGS_POPUP_VALUES} from '@/consts/aem/aemDeviceSettingsPopupValueTypes';
import {AEM_DEVICE_SETTINGS_TYPES} from '@/consts/aem/aemDeviceSettingsTypes';
import {LED_MODE_TYPES} from '@/consts/uam/ledModeTypes';
import {getLockStatusByBool} from '@/consts/uam/lockUnlockTypes';
import {getOnOffTypeByValue} from '@/consts/uam/onOffTypes';
import appContent from '@/services/content/appContent';
import mwService from '@/services/mw/mwService';
import {selectDeviceType, selectIsDeviceConnectedToIot, selectIsHolderConnectedToIot} from '@/state/selectors/device';
import {
    selectAutoStart,
    selectDeviceLockStatus,
    selectFlexPuff,
    selectLedIntensity,
} from '@/state/selectors/deviceSettings';
import {updateDeviceSettingState} from '@/state/slices/deviceSlice';
import {dispatch, getState} from '@/state/store';

import {getIsSettingHolderRelated} from '../device/deviceSettingsService';

const startAutoClean = ({onSuccess, onError}) => {
    autoClean({onSuccess, onError});
};

const resetDevice = ({isHolder, onSuccess, onError}) => {
    resetMyDevice({isHolder, onSuccess, onError});
};

const findDevice = ({action, durationSeconds = 30, onSuccess, onError}) => {
    findMyDevice({action, durationSeconds, onSuccess, onError});
};

//data is keyValue array model {[key]: value, [key]: value} key is popupValueType, value is bool eg {[SMART_GESTURES_LIFT_UP]: true}
const updateDeviceSettings = ({settingType, data, onUpdateStart, onSuccess, onError}) => {
    if (isCurrentValuesSame(data)) return;

    const state = getState();
    const isDeviceConnectedToIot = selectIsDeviceConnectedToIot(state);
    const isHolderConnectedToIot = selectIsHolderConnectedToIot(state);

    if (!isHolderConnectedToIot && !isDeviceConnectedToIot) return onError();

    onUpdateStart?.();

    const deviceType = selectDeviceType(state);
    const isHolderRelated = getIsSettingHolderRelated(deviceType, settingType);

    const onSuccessHandler = (isHolder, deviceData) => {
        dispatch(updateDeviceSettingState({isHolder, deviceData}));
        onSuccess?.();
    };

    switch (settingType) {
        case AEM_DEVICE_SETTINGS_TYPES.AUTO_START:
            const autoStartMode = getOnOffPopupValue(data, DEVICE_SETTINGS_POPUP_VALUES.AUTO_START);

            updateAutoStart({
                autoStartMode,
                isHolder: isHolderRelated,
                onSuccess: (autoStartData) => onSuccessHandler(isHolderRelated, autoStartData),
                onError,
            });
            break;
        case AEM_DEVICE_SETTINGS_TYPES.FLEX_BATTERY:
            break;
        case AEM_DEVICE_SETTINGS_TYPES.FLEX_PUFF:
            const flexPuffMode = getOnOffPopupValue(data, DEVICE_SETTINGS_POPUP_VALUES.FLEX_PUFF);

            updateFlexPuff({
                flexPuffMode,
                isHolder: isHolderRelated,
                onSuccess: (autoStartData) => onSuccessHandler(isHolderRelated, autoStartData),
                onError,
            });
            break;
        case AEM_DEVICE_SETTINGS_TYPES.ILLUMINATION_MODE:
            const isLowMode = isPopupValueTypeExists(data, DEVICE_SETTINGS_POPUP_VALUES.ILLUMINATION_MODE_LOW);
            const ledMode = isLowMode ? LED_MODE_TYPES.NIGHT_MODE : LED_MODE_TYPES.DAY_MODE;

            updateLed({ledMode, onSuccess, onError});
            break;
        case AEM_DEVICE_SETTINGS_TYPES.LOCK_UNLOCK:
            const lockUnlockValue = getBoolPopupValue(data, AEM_DEVICE_SETTINGS_TYPES.LOCK_UNLOCK);

            updateLock({
                lockUnlockValue,
                isHolder: isHolderRelated,
                onSuccess: (lockData) => onSuccessHandler(isHolderRelated, lockData),
                onError,
            });
            break;
        case AEM_DEVICE_SETTINGS_TYPES.PAUSE_MODE:
        case AEM_DEVICE_SETTINGS_TYPES.SMART_GESTURES:
        case AEM_DEVICE_SETTINGS_TYPES.VIBRATIONS:
        default:
            break;
    }
};

const isCurrentValuesSame = (popupValues) => {
    return Object.entries(popupValues).every(([key, value]) => isCurrentValueSame(key, value));
};

const isCurrentValueSame = (popupValueType, value) => {
    const state = getState();

    switch (popupValueType) {
        case DEVICE_SETTINGS_POPUP_VALUES.AUTO_START:
            const autoStart = selectAutoStart(state);

            return autoStart === getOnOffTypeByValue(value);
        case DEVICE_SETTINGS_POPUP_VALUES.FLEX_PUFF:
            const flexPuff = selectFlexPuff(state);

            return flexPuff === getOnOffTypeByValue(value);
        case DEVICE_SETTINGS_POPUP_VALUES.PAUSE_MODE:
            break;
        case DEVICE_SETTINGS_POPUP_VALUES.FLEX_BATTERY_PERFORMANCE_MODE:
            break;
        case DEVICE_SETTINGS_POPUP_VALUES.FLEX_BATTERY_ECO_MODE:
            break;
        case DEVICE_SETTINGS_POPUP_VALUES.SMART_GESTURES_LIFT_UP:
            break;
        case DEVICE_SETTINGS_POPUP_VALUES.ILLUMINATION_MODE_LOW:
            const curLedIntensity = selectLedIntensity(state);

            return curLedIntensity === LED_MODE_TYPES.NIGHT_MODE;
        case DEVICE_SETTINGS_POPUP_VALUES.ILLUMINATION_MODE_STRONG:
            const ledIntensity = selectLedIntensity(state);

            return ledIntensity === LED_MODE_TYPES.DAY_MODE;
        case DEVICE_SETTINGS_POPUP_VALUES.VIBRATIONS_BATTERY_CHARGED:
            break;
        case DEVICE_SETTINGS_POPUP_VALUES.VIBRATIONS_SMART_HEATING:
            break;
        case DEVICE_SETTINGS_POPUP_VALUES.VIBRATIONS_ALMOST_FINISHED:
            break;
        case DEVICE_SETTINGS_POPUP_VALUES.VIBRATIONS_READY_TO_USE:
            break;
        case DEVICE_SETTINGS_POPUP_VALUES.VIBRATIONS_EXPERIENCE_ENDED:
            break;
        //use setting type for settings without individual setting popup
        case AEM_DEVICE_SETTINGS_TYPES.LOCK_UNLOCK:
            const deviceLockStatus = selectDeviceLockStatus(state);
            const selectedLockStatus = getLockStatusByBool(value);

            return deviceLockStatus === selectedLockStatus;
        default:
            break;
    }
};

const isPopupValueTypeExists = (popupValues, popupValueType) => {
    const popupValue = popupValues[popupValueType];

    return !!popupValue;
};

const getPopupValue = (popupValues, popupValueType) => {
    const popupValue = popupValues[popupValueType];

    return popupValue;
};

const getOnOffPopupValue = (popupValues, popupValueType) => {
    const value = getPopupValue(popupValues, popupValueType);

    return getOnOffTypeByValue(value);
};

const getBoolPopupValue = (popupValues, popupValueType) => {
    return !!getPopupValue(popupValues, popupValueType);
};

const updateLed = ({ledMode, onSuccess, onError}) => {
    let isChargerSuccessfullyUpdated = false;
    const isHolderConnectedToIot = selectIsHolderConnectedToIot(getState());

    const onHolderLedFailed = () => {
        const {title, description} = appContent.getHolderSettingChangeFailedNotification();

        showToast(title, {description});
    };

    const onFlowError = () => {
        if (isChargerSuccessfullyUpdated) {
            //show notification error for holder if flow ended with error but charger successfully updated
            onHolderLedFailed();
            onSuccess?.();
            return;
        }
        onError?.();
    };

    const {onSuccessHandler, onErrorHandler} = mwService.validateFlowEnded({
        onSuccess,
        onError: onFlowError,
        isWaitHolder: isHolderConnectedToIot,
    });

    const ledData = {ledIntensity: {intensity: ledMode}};

    const onSuccessUpdateHandler = (isHolder) => {
        if (!isHolder) {
            isChargerSuccessfullyUpdated = true;
        }

        dispatch(updateDeviceSettingState({isHolder, deviceData: ledData}));
        onSuccessHandler();
    };

    const data = {
        settingName: 'ledIntensity',
        newValue: {intensity: ledMode},
    };

    updateDeviceSettingWithUamCheck({onSuccess: () => onSuccessUpdateHandler(), onError: onErrorHandler, data});

    if (isHolderConnectedToIot) {
        updateDeviceSettingWithUamCheck({
            onSuccess: () => onSuccessUpdateHandler(true),
            onError: onErrorHandler,
            data,
            isHolder: true,
        });
    }
};

//lockUnlockValue is bool
const updateLock = ({lockUnlockValue, isHolder, onSuccess, onError}) => {
    const protectionType = getLockStatusByBool(lockUnlockValue);
    const lockData = {parentProtection: {protectionType}};

    const data = {
        settingName: 'parentProtection',
        newValue: {protectionType, timeout: null},
    };

    const onSuccessHandler = () => {
        onSuccess(lockData);
    };

    updateDeviceSettingWithUamCheck({onSuccess: onSuccessHandler, onError, data, isHolder});
};

const updateAutoStart = ({autoStartMode, isHolder, onSuccess, onError}) => {
    const autoStartData = {autoStartHeating: {featureState: autoStartMode}};

    const data = {
        settingName: 'autoStartHeating',
        newValue: {featureState: autoStartMode},
    };

    const onSuccessHandler = () => {
        onSuccess(autoStartData);
    };

    updateDeviceSettingWithUamCheck({onSuccess: onSuccessHandler, onError, data, isHolder});
};

const updateFlexPuff = ({flexPuffMode, isHolder, onSuccess, onError}) => {
    const flexPuffData = {engineJoule: {featureState: flexPuffMode}};

    const data = {
        settingName: 'engineJoule',
        newValue: {featureState: flexPuffMode},
    };

    const onSuccessHandler = () => {
        onSuccess(flexPuffData);
    };

    updateDeviceSettingWithUamCheck({onSuccess: onSuccessHandler, onError, data, isHolder});
};

const updateDeviceSettingWithUamCheck = async ({onSuccess, onError, data, isHolder, isRetry}) => {
    try {
        await mwService.checkAndSetupUamToken(isHolder);
    } catch (error) {
        onError?.(error);
        return;
    }

    const onMwError = (error) => {
        mwService.validateMwError({
            isRetry,
            error,
            isHolder,
            onError,
            onStartAgainHandler: () => {
                updateDeviceSettingWithUamCheck({onSuccess, onError, data, isHolder, isRetry: true});
            },
        });
    };

    updateDeviceSetting({onSuccess, onError: onMwError, data, isHolder});
};

export default {updateDeviceSettings, resetDevice, findDevice, startAutoClean};
